import type { ScoreExplanation } from "./factor-explanations";
import type { ScoreEvidenceTransport } from "./score-evidence";
import type { SafetyHeadsUpInput, SafetyHazard } from "./safety-headsup";
import type { NullableHourlyScore } from "./session-window-universal";

export function forecastIsStale(lastUpdated: string | null | undefined, flagged = false, now = Date.now()): boolean {
  const timestamp = lastUpdated ? Date.parse(lastUpdated) : NaN;
  return flagged || !Number.isFinite(timestamp) || now - timestamp > 36 * 60 * 60 * 1000;
}

/** No past dates may be presented as the coming week. */
export function upcomingForecast<T extends { date: string }>(days: readonly T[], today: string): T[] {
  const end = new Date(`${today}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return days.filter(({ date }) => date >= today && date <= end.toISOString().slice(0, 10))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Use the same scored factors as the detailed explanation, without independent claims. */
export function evidenceVerdict(explanation: ScoreExplanation | null | undefined): string | null {
  if (!explanation) return null;
  const limiter = explanation.factors.find((factor) => factor.influence === "main limiter");
  const positive = explanation.factors.find((factor) => factor.influence === "strong positive");
  const summary = [limiter?.why, positive?.why].filter(Boolean).map((why) => why!.split(/(?<=[.!?])\s/)[0]).join(" ");
  return summary.length > 260 ? `${summary.slice(0, 257).replace(/\s+\S*$/, "")}…` : summary || null;
}

/** Retain every known warning; unavailable checks can never produce a green all-clear. */
export function forecastSafety(
  input: SafetyHeadsUpInput | undefined,
  explanation: ScoreExplanation | null | undefined,
  evidence: ScoreEvidenceTransport | null | undefined,
  stale: boolean,
): SafetyHeadsUpInput {
  if (stale) return { hazards: [], dataState: "stale" };
  const canonical: SafetyHazard[] = (explanation?.safety ?? []).flatMap((row) =>
    row.severity === "red" || row.severity === "amber"
      ? [{ severity: row.severity === "red" ? "danger" : "caution", label: row.why === "Safety status retained from the conditions scoring run." ? row.label : `${row.label}: ${row.why}` }]
      : []);
  if (canonical.length === 0 && (evidence?.safetyState === "flagged-red" || evidence?.safetyState === "flagged-amber")) {
    const severity = evidence.safetyState === "flagged-red" ? "danger" : "caution";
    canonical.push(...(evidence.safetyNotes.length ? evidence.safetyNotes : ["A scored safety check is flagged. Review the detailed safety factors."]).map((label) => ({ severity, label }) as SafetyHazard));
  }
  const hazards = [...new Map([...canonical, ...(input?.hazards ?? [])].map((hazard) => [hazard.label, hazard])).values()];
  const knownWarning = hazards.some(({ severity }) => severity === "danger" || severity === "caution");
  const unavailable = input?.dataState === "missing" || evidence?.safetyState === "no-data" ||
    explanation?.safety.some(({ severity }) => severity === "unavailable" || severity === "unknown");
  if (knownWarning && unavailable) hazards.push({ severity: "info", label: "Some safety checks are unavailable. Check local advice before setting out." });
  return { hazards, dataState: unavailable && !knownWarning ? "missing" : "present", checksIncomplete: Boolean(unavailable) };
}

export type DaylightWindow = { start: number; end: number; score: number };

/** Leave the session planner only windows whose start has not passed locally. */
export function remainingForecastHours<T extends { dayOffset: number; hour: number }>(hours: readonly T[], localHour: number): T[] {
  return hours.filter((hour) => hour.dayOffset > 0 || (hour.dayOffset === 0 && hour.hour >= Math.ceil(localHour)));
}

/** Consecutive daylight hours only, with no interpolation across missing scores. */
export function bestDaylightWindow(scores: readonly NullableHourlyScore[], fromHour = 0, duration = 2): DaylightWindow | null {
  const ordered = [...scores].sort((a, b) => a.hour - b.hour);
  let best: DaylightWindow | null = null;
  for (let i = 0; i <= ordered.length - duration; i++) {
    const block = ordered.slice(i, i + duration);
    const start = block[0].hour;
    if (start < fromHour || block.some((point, index) => !point.isDay || point.hour !== start + index || point.score === null || !Number.isFinite(point.score) || point.score < 5)) continue;
    const score = block.reduce((sum, point) => sum + (point.score ?? 0), 0) / duration;
    if (!best || score > best.score) best = { start, end: start + duration, score };
  }
  return best;
}

export type WindUnit = "kmh" | "kn";
export function formatWind(kmh: number | null | undefined, unit: WindUnit = "kmh"): string {
  if (kmh == null || !Number.isFinite(kmh)) return "Unavailable";
  return `${Math.round(unit === "kn" ? kmh / 1.852 : kmh)} ${unit === "kn" ? "kn" : "km/h"}`;
}
